import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

type Period = 'today' | '7d' | '30d' | '90d' | 'all';

export default function CEODashboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('30d');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [searchConsoleConnected, setSearchConsoleConnected] = useState(false);

  // Data states
  const [overview, setOverview] = useState<any>(null);
  const [globalStats, setGlobalStats] = useState<any>(null);
  const [usersAnalytics, setUsersAnalytics] = useState<any>(null);
  const [compressionAnalytics, setCompressionAnalytics] = useState<any>(null);
  const [revenue, setRevenue] = useState<any>(null);
  const [engagement, setEngagement] = useState<any>(null);
  const [security, setSecurity] = useState<any>(null);
  const [mobileStats, setMobileStats] = useState<any>(null);
  const [operations, setOperations] = useState<any>(null);
  const [searchConsoleData, setSearchConsoleData] = useState<any>(null);

  useEffect(() => {
    fetchData();
    checkSearchConsoleStatus();
  }, [period]);

  const checkSearchConsoleStatus = async () => {
    const token = localStorage.getItem('jwt');
    try {
      const res = await fetch(`${API_BASE_URL}/search-console/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSearchConsoleConnected(data.connected);

      if (data.connected) {
        fetchSearchConsoleData();
      }
    } catch (error) {
      console.error('Search Console status error:', error);
    }
  };

  const fetchSearchConsoleData = async () => {
    const token = localStorage.getItem('jwt');
    try {
      const [summaryRes, queriesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/search-console/summary?startDate=${getDateDaysAgo(30)}&endDate=${getDateDaysAgo(0)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/search-console/top-queries?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (summaryRes.ok && queriesRes.ok) {
        const summary = await summaryRes.json();
        const queries = await queriesRes.json();
        setSearchConsoleData({ summary, queries: queries.queries });
      }
    } catch (error) {
      console.error('Search Console data error:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('jwt');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [
        overviewRes,
        globalStatsRes,
        usersRes,
        compressionRes,
        operationsRes,
        revenueRes,
        engagementRes,
        securityRes,
        mobileRes
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/ceo-dashboard/overview?period=${period}`, { headers }),
        fetch(`${API_BASE_URL}/feed/stats`),
        fetch(`${API_BASE_URL}/ceo-dashboard/users-analytics?period=${period}`, { headers }),
        fetch(`${API_BASE_URL}/ceo-dashboard/compression-analytics?period=${period}`, { headers }),
        fetch(`${API_BASE_URL}/ceo-dashboard/operations?period=${period}&days=30`, { headers }),
        fetch(`${API_BASE_URL}/ceo-dashboard/revenue?period=${period}`, { headers }),
        fetch(`${API_BASE_URL}/ceo-dashboard/engagement?period=${period}`, { headers }),
        fetch(`${API_BASE_URL}/ceo-dashboard/security?period=${period}`, { headers }),
        fetch(`${API_BASE_URL}/ceo-dashboard/mobile-stats?period=${period}`, { headers })
      ]);

      if (overviewRes.status === 403) {
        alert('Access denied. CEO/Admin only.');
        navigate('/');
        return;
      }

      setOverview(await overviewRes.json());
      setGlobalStats(await globalStatsRes.json());
      setUsersAnalytics(await usersRes.json());
      setCompressionAnalytics(await compressionRes.json());
      setOperations(operationsRes.ok ? await operationsRes.json() : null);
      setRevenue(await revenueRes.json());
      setEngagement(await engagementRes.json());
      setSecurity(await securityRes.json());
      setMobileStats(await mobileRes.json());
    } catch (error) {
      console.error('Error fetching CEO dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateDaysAgo = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen pt-12 bg-white flex items-center justify-center">
        <div className="text-[#1d1d1f] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-12 bg-white text-[#1d1d1f] flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="w-full lg:w-64 bg-white border-b lg:border-b-0 lg:border-r border-black/[0.06] flex-shrink-0">
        <div className="p-4 lg:p-6 border-b border-black/[0.06]">
          <h1 className="text-lg lg:text-xl font-semibold">SlimFile CEO</h1>
          <p className="text-[#86868b] text-xs lg:text-sm mt-1">Analytics Dashboard</p>
        </div>

        <nav className="p-2 lg:p-4 flex lg:flex-col overflow-x-auto lg:overflow-x-visible">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'users', label: 'Users' },
            { id: 'compressions', label: 'Compressions' },
            { id: 'operations', label: 'Operations' },
            { id: 'revenue', label: 'Revenue' },
            { id: 'engagement', label: 'Engagement' },
            { id: 'mobile', label: 'Mobile' },
            { id: 'security', label: 'Security' },
            { id: 'analytics', label: 'Analytics' },
            { id: 'search', label: 'Search' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`whitespace-nowrap lg:whitespace-normal lg:w-full text-left px-3 lg:px-4 py-2 lg:py-3 rounded-lg mb-0 lg:mb-1 mr-2 lg:mr-0 transition-colors text-sm ${
                activeSection === item.id
                  ? 'bg-[#e8e8ed] text-[#1d1d1f] font-medium'
                  : 'text-[#6e6e73] hover:bg-black/[0.05] hover:text-[#1d1d1f]'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        {/* Header */}
        <header className="bg-white border-b border-black/[0.06] px-4 lg:px-8 py-3 lg:py-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-0">
          <div>
            <h2 className="text-xl lg:text-2xl font-semibold capitalize">{activeSection}</h2>
            <p className="text-[#86868b] text-xs lg:text-sm mt-1">
              {period === 'today' ? 'Today' : period === '7d' ? 'Last 7 days' : period === '30d' ? 'Last 30 days' : period === '90d' ? 'Last 90 days' : 'All time'}
            </p>
          </div>

          {/* Period Filter */}
          <div className="flex gap-2 overflow-x-auto w-full lg:w-auto">
            {(['today', '7d', '30d', '90d', 'all'] as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm transition-colors whitespace-nowrap ${
                  period === p
                    ? 'bg-red-600 text-white'
                    : 'bg-[#f5f5f7] text-[#6e6e73] hover:bg-black/[0.05] hover:text-[#1d1d1f]'
                }`}
              >
                {p === 'today' ? 'Today' : p === '7d' ? '7D' : p === '30d' ? '30D' : p === '90d' ? '90D' : 'All'}
              </button>
            ))}
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
          {activeSection === 'overview' && overview && globalStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatCard
                  label="Total Users"
                  value={overview.users.total.toLocaleString()}
                  subtitle={`${overview.users.web} web, ${overview.users.mobile} mobile`}
                />
                <StatCard
                  label="Compressions"
                  value={globalStats.totalCompressions.toLocaleString()}
                  subtitle={`${(globalStats.totalSpaceSaved / (1024 * 1024 * 1024)).toFixed(2)} GB saved`}
                />
                <StatCard
                  label="B2B Customers"
                  value={overview.revenue.b2bCustomers}
                  subtitle={`${overview.revenue.apiKeys} API keys`}
                />
                <StatCard
                  label="Avg Rating"
                  value={overview.engagement.avgRating}
                  subtitle={`${overview.engagement.reviews} reviews`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                <StatCard
                  label="Guest Actions"
                  value={overview.engagement.guestActions.toLocaleString()}
                />
                <StatCard
                  label="PWA Installs"
                  value={overview.engagement.pwaInstalls}
                />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-6">
                {/* User Growth Chart */}
                {usersAnalytics && usersAnalytics.growth.web.length > 0 && (
                  <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                    <h3 className="text-base lg:text-lg font-semibold mb-4">User Growth Trend</h3>
                    <div className="relative h-48">
                      <svg className="w-full h-full" viewBox="0 0 300 150" preserveAspectRatio="none">
                        <polyline
                          fill="none"
                          stroke="#dc2626"
                          strokeWidth="2"
                          points={usersAnalytics.growth.web.map((day: any, i: number, arr: any[]) => {
                            const maxCount = Math.max(...arr.map((d: any) => d.count), 1);
                            const x = (i / (arr.length - 1 || 1)) * 300;
                            const y = 150 - ((day.count / maxCount) * 140);
                            return `${x},${y}`;
                          }).join(' ')}
                        />
                        <polyline
                          fill="rgba(220, 38, 38, 0.08)"
                          stroke="none"
                          points={`0,150 ${usersAnalytics.growth.web.map((day: any, i: number, arr: any[]) => {
                            const maxCount = Math.max(...arr.map((d: any) => d.count), 1);
                            const x = (i / (arr.length - 1 || 1)) * 300;
                            const y = 150 - ((day.count / maxCount) * 140);
                            return `${x},${y}`;
                          }).join(' ')} 300,150`}
                        />
                      </svg>
                    </div>
                    <p className="text-xs text-[#86868b] mt-2">{period === 'today' ? 'Today' : period === '7d' ? 'Last 7 days' : period === '30d' ? 'Last 30 days' : period === '90d' ? 'Last 90 days' : 'All time'} • Total: {usersAnalytics.growth.web.reduce((sum: number, d: any) => sum + d.count, 0)} users</p>
                  </div>
                )}

                {/* Compression Trend Chart */}
                {compressionAnalytics && compressionAnalytics.trend.length > 0 && (
                  <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                    <h3 className="text-base lg:text-lg font-semibold mb-4">Compression Trend</h3>
                    <div className="relative h-48">
                      <svg className="w-full h-full" viewBox="0 0 300 150" preserveAspectRatio="none">
                        <polyline
                          fill="none"
                          stroke="#dc2626"
                          strokeWidth="2"
                          points={compressionAnalytics.trend.map((day: any, i: number, arr: any[]) => {
                            const maxCount = Math.max(...arr.map((d: any) => d.count), 1);
                            const x = (i / (arr.length - 1 || 1)) * 300;
                            const y = 150 - ((day.count / maxCount) * 140);
                            return `${x},${y}`;
                          }).join(' ')}
                        />
                        <polyline
                          fill="rgba(220, 38, 38, 0.08)"
                          stroke="none"
                          points={`0,150 ${compressionAnalytics.trend.map((day: any, i: number, arr: any[]) => {
                            const maxCount = Math.max(...arr.map((d: any) => d.count), 1);
                            const x = (i / (arr.length - 1 || 1)) * 300;
                            const y = 150 - ((day.count / maxCount) * 140);
                            return `${x},${y}`;
                          }).join(' ')} 300,150`}
                        />
                      </svg>
                    </div>
                    <p className="text-xs text-[#86868b] mt-2">{period === 'today' ? 'Today' : period === '7d' ? 'Last 7 days' : period === '30d' ? 'Last 30 days' : period === '90d' ? 'Last 90 days' : 'All time'} • Total: {compressionAnalytics.trend.reduce((sum: number, d: any) => sum + d.count, 0)} compressions</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'users' && usersAnalytics && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                <StatCard label="Web Users" value={usersAnalytics.growth.web.reduce((sum: number, d: any) => sum + d.count, 0)} />
                <StatCard label="Mobile Users" value={usersAnalytics.growth.mobile.reduce((sum: number, d: any) => sum + d.count, 0)} />
                <StatCard label="Top Users" value={usersAnalytics.topUsers.length} />
              </div>

              <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                <h3 className="text-base lg:text-lg font-semibold mb-4">Top Users by Compressions</h3>
                <div className="space-y-2 overflow-x-auto">
                  {usersAnalytics.topUsers.slice(0, 10).map((user: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#f5f5f7] rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{user.name}</p>
                        <p className="text-[#86868b] text-xs truncate">{user.email}</p>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <p className="font-semibold text-sm">{user.totalFilesCompressed}</p>
                        <p className="text-[#86868b] text-xs">{(user.totalSpaceSaved / (1024 * 1024)).toFixed(1)} MB</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'compressions' && compressionAnalytics && globalStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                <StatCard
                  label="Total Compressions"
                  value={globalStats.totalCompressions.toLocaleString()}
                />
                <StatCard
                  label="Avg Compression Ratio"
                  value={globalStats.avgCompressionRatio + '%'}
                />
              </div>

              <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                <h3 className="text-base lg:text-lg font-semibold mb-4">File Types</h3>
                <div className="space-y-2">
                  {compressionAnalytics.fileTypes.map((ft: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#f5f5f7] rounded-lg">
                      <span className="font-semibold text-sm">{ft._id}</span>
                      <div className="text-right">
                        <p className="font-semibold text-sm">{ft.count.toLocaleString()}</p>
                        <p className="text-[#86868b] text-xs">{ft.avgCompressionRatio.toFixed(1)}% avg ratio</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'operations' && operations && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                <StatCard label="Total Feature Uses" value={num(operations.totalUses)} />
                <StatCard label="Operations Tracked" value={operations.totalOperations} />
                <StatCard label="Unique Visitors" value={num(operations.uniqueVisitors)} />
              </div>

              <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                <h3 className="text-base lg:text-lg font-semibold mb-1">All Operations</h3>
                <p className="text-[#86868b] text-xs lg:text-sm mb-4">
                  Counted from the dedicated usage collections on every successful call, so signed-out
                  visitors are included and nothing here depends on a compression ratio.
                  OCR and QR run entirely in your browser, so the page reports its own visits and
                  completions rather than a server request.
                  <span className="block mt-1">
                    <span className="text-[#6e6e73]">Compression</span> is the same counter the Feed page
                    shows, so it matches exactly and ignores the period filter. Everything else respects it.
                  </span>
                </p>

                {operations.operations.length === 0 ? (
                  <p className="text-[#86868b] text-sm py-6 text-center">No usage recorded for this period.</p>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[#86868b] text-left border-b border-black/[0.06]">
                            <th className="pb-3 pr-4 font-medium">Operation</th>
                            <th className="pb-3 pr-4 font-medium text-right">Uses</th>
                            <th className="pb-3 pr-4 font-medium text-right">Signed in</th>
                            <th className="pb-3 pr-4 font-medium text-right">Anonymous</th>
                            <th className="pb-3 pr-4 font-medium text-right">Visitors</th>
                            <th className="pb-3 pr-4 font-medium text-right">Pages</th>
                            <th className="pb-3 pr-4 font-medium text-right">Visit / Done</th>
                            <th className="pb-3 pr-4 font-medium text-right">Avg in</th>
                            <th className="pb-3 font-medium text-right">Last used</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.operations.map((op: any) => (
                            <tr key={op.collection + op.operation} className="border-b border-black/[0.06]">
                              <td className="py-3 pr-4">
                                <span className="font-semibold">{formatOperation(op.operation)}</span>
                                <span className="block text-[#86868b] text-xs">
                                  {op.collection}{op.lifetime && <span className="text-[#6e6e73]"> · all-time</span>}
                                </span>
                              </td>
                              <td className="py-3 pr-4 text-right font-semibold">{num(op.uses)}</td>
                              <td className="py-3 pr-4 text-right text-[#6e6e73]">{num(op.signedIn)}</td>
                              <td className="py-3 pr-4 text-right text-[#6e6e73]">{num(op.anonymous)}</td>
                              <td className="py-3 pr-4 text-right text-[#6e6e73]">
                                {num(op.uniqueVisitors)}
                              </td>
                              <td className="py-3 pr-4 text-right text-[#6e6e73]">{op.pages ?? '-'}</td>
                              <td className="py-3 pr-4 text-right text-[#6e6e73] whitespace-nowrap">
                                {op.visits || op.completes
                                  ? `${num(op.visits)} / ${num(op.completes)}`
                                  : '-'}
                                {op.completionRate !== null && (
                                  <span className="block text-[#86868b] text-xs">{op.completionRate}% done</span>
                                )}
                              </td>
                              <td className="py-3 pr-4 text-right text-[#6e6e73]">
                                {op.compressionAvgRatio ? `${op.compressionAvgRatio}% avg` : formatBytes(op.originalBytes / op.uses)}
                              </td>
                              <td className="py-3 text-right text-[#86868b] text-xs whitespace-nowrap">
                                {new Date(op.lastUsedAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                      {operations.operations.map((op: any) => (
                        <div key={op.collection + op.operation} className="p-3 bg-[#f5f5f7] rounded-lg">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-sm">{formatOperation(op.operation)}</p>
                              <p className="text-[#86868b] text-xs">
                                {op.collection}{op.lifetime && <span className="text-[#6e6e73]"> · all-time</span>}
                              </p>
                            </div>
                            <p className="text-lg font-semibold shrink-0">{num(op.uses)}</p>
                          </div>
                          <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                            <div>
                              <p className="text-[#86868b]">Visits</p>
                              <p className="font-semibold">{num(op.visits)}</p>
                            </div>
                            <div>
                              <p className="text-[#86868b]">Completed</p>
                              <p className="font-semibold">{num(op.completes)}</p>
                            </div>
                            <div>
                              <p className="text-[#86868b]">Signed in</p>
                              <p className="font-semibold">{num(op.signedIn)}</p>
                            </div>
                            <div>
                              <p className="text-[#86868b]">Anonymous</p>
                              <p className="font-semibold">{num(op.anonymous)}</p>
                            </div>
                            <div>
                              <p className="text-[#86868b]">Visitors</p>
                              <p className="font-semibold">
                                {num(op.uniqueVisitors)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeSection === 'revenue' && revenue && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                <StatCard label="Total Customers" value={revenue.b2b.total} />
                <StatCard label="Active" value={revenue.b2b.active} />
                <StatCard label="Inactive" value={revenue.b2b.inactive} />
              </div>

              <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                <h3 className="text-base lg:text-lg font-semibold mb-4">B2B Customers</h3>
                <div className="space-y-2 overflow-x-auto">
                  {revenue.b2b.customers.map((customer: any, i: number) => (
                    <div key={i} className="flex flex-col lg:flex-row lg:items-center justify-between p-3 bg-[#f5f5f7] rounded-lg gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm">{customer.customerName}</p>
                        <p className="text-[#86868b] text-xs">{customer.customerCompany || 'N/A'}</p>
                      </div>
                      <div className="flex gap-4 text-xs">
                        <div>
                          <p className="text-[#86868b]">Usage</p>
                          <p className="font-semibold">{customer.usageCount}</p>
                        </div>
                        <div>
                          <p className="text-[#86868b]">Status</p>
                          <p className={customer.isActive ? 'text-emerald-600' : 'text-red-600'}>
                            {customer.isActive ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'engagement' && engagement && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                <StatCard label="PWA Installs" value={engagement.pwa.byPlatform.reduce((sum: number, p: any) => sum + p.count, 0)} />
                <StatCard label="Reviews" value={engagement.reviews.breakdown.reduce((sum: number, r: any) => sum + r.count, 0)} />
                <StatCard label="Total Guests" value={engagement.guests.totalGuests || 0} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                  <h3 className="text-base lg:text-lg font-semibold mb-4">PWA by Platform</h3>
                  <div className="space-y-2">
                    {engagement.pwa.byPlatform.map((p: any, i: number) => (
                      <div key={i} className="flex justify-between p-2 bg-[#f5f5f7] rounded">
                        <span className="capitalize text-sm">{p._id || 'Unknown'}</span>
                        <span className="font-semibold text-sm">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                  <h3 className="text-base lg:text-lg font-semibold mb-4">Review Breakdown</h3>
                  <div className="space-y-2">
                    {engagement.reviews.breakdown.map((r: any, i: number) => (
                      <div key={i} className="flex justify-between p-2 bg-[#f5f5f7] rounded">
                        <span className="text-sm">{r._id} Stars</span>
                        <span className="font-semibold text-sm">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'mobile' && mobileStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                <StatCard label="Total Users" value={mobileStats.total} />
                <StatCard label="Active (30d)" value={mobileStats.active} />
                <StatCard label="Top Users" value={mobileStats.topUsers.length} />
              </div>

              <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                <h3 className="text-base lg:text-lg font-semibold mb-4">Top Mobile Users</h3>
                <div className="space-y-2 overflow-x-auto">
                  {mobileStats.topUsers.slice(0, 10).map((user: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#f5f5f7] rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{user.name}</p>
                        <p className="text-[#86868b] text-xs truncate">{user.email}</p>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <p className="font-semibold text-sm">{user.totalFilesCompressed}</p>
                        <p className="text-[#86868b] text-xs">{(user.totalSpaceSaved / (1024 * 1024)).toFixed(1)} MB</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'security' && security && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                <StatCard label="Security Events" value={security.events.length} />
                <StatCard label="Security Accesses" value={security.accesses.length} />
              </div>

              <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                <h3 className="text-base lg:text-lg font-semibold mb-4">Recent Events</h3>
                <div className="space-y-2 overflow-x-auto max-h-96 overflow-y-auto">
                  {security.events.slice(0, 20).map((event: any, i: number) => (
                    <div key={i} className="p-3 bg-[#f5f5f7] rounded-lg text-xs">
                      <p className="font-semibold">{event.type || 'Unknown'}</p>
                      <p className="text-[#86868b] mt-1">{new Date(event.createdAt).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'analytics' && (
            <div className="bg-[#f5f5f7] rounded-lg p-1 h-full min-h-[600px] lg:min-h-[800px]">
              <iframe
                src="https://datastudio.google.com/embed/reporting/336ae76d-a21e-493c-b697-d936274fcb5a/page/kIV1C"
                className="w-full h-full rounded-lg"
                style={{ minHeight: '600px' }}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          )}

          {activeSection === 'search' && (
            <div className="space-y-6">
              {!searchConsoleConnected ? (
                <div className="text-center py-12">
                  <p className="text-[#86868b] mb-4">Connect Google Search Console to see analytics</p>
                  <button
                    className="px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 text-sm lg:text-base"
                    onClick={() => window.location.href = `${API_BASE_URL}/search-console/auth`}
                  >
                    Connect Search Console
                  </button>
                </div>
              ) : searchConsoleData ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-6">
                    <StatCard label="Total Clicks" value={searchConsoleData.summary.totalClicks} />
                    <StatCard label="Impressions" value={searchConsoleData.summary.totalImpressions} />
                    <StatCard label="Avg CTR" value={searchConsoleData.summary.avgCTR} />
                    <StatCard label="Avg Position" value={searchConsoleData.summary.avgPosition} />
                  </div>

                  <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
                    <h3 className="text-base lg:text-lg font-semibold mb-4">Top Search Queries</h3>
                    <div className="space-y-2 overflow-x-auto">
                      {searchConsoleData.queries.map((query: any, i: number) => (
                        <div key={i} className="flex flex-col lg:flex-row lg:items-center justify-between p-3 bg-[#f5f5f7] rounded-lg gap-2">
                          <span className="font-semibold text-sm min-w-0 flex-1 truncate">{query.query}</span>
                          <div className="flex gap-4 text-xs flex-shrink-0">
                            <div>
                              <p className="text-[#86868b]">Clicks</p>
                              <p className="font-semibold">{query.clicks}</p>
                            </div>
                            <div>
                              <p className="text-[#86868b]">CTR</p>
                              <p className="font-semibold">{query.ctr}</p>
                            </div>
                            <div>
                              <p className="text-[#86868b]">Position</p>
                              <p className="font-semibold">{query.position}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-[#86868b]">
                  Loading Search Console data...
                </div>
              )}
            </div>
          )}

          {loading && (
            <div className="text-center py-12 text-[#86868b]">
              Loading data...
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// The compression row comes from the Stats counter, which has no anonymous or
// visitor split, so those fields are null. Format defensively everywhere
// rather than assume every row came from the same aggregation.
function num(value: number | null | undefined): string {
  return value === null || value === undefined ? '-' : value.toLocaleString();
}

function formatBytes(bytes: number) {
  if (!bytes || !isFinite(bytes)) return '-';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatOperation(op: string) {
  const labels: Record<string, string> = {
    'scan-compress': 'Scan & Compress',
    'scan-merge': 'Batch Scan & Merge',
    'scan-split': 'Batch Scan & Split',
    'scan-convert': 'Scan & Convert',
    'forge-merge': 'PDF Merge',
    'forge-split': 'PDF Split',
    'pdf-protect': 'Password Protect',
    'pdf-unlock': 'Remove Password',
    'b2b-compress': 'B2B API Compress',
    'ai-grok': 'AI Lab',
    ocr: 'OCR Tool',
    'zip-compress': 'Zip Files & Folders',
    qr: 'QR Code Generator',
    compress: 'Compress Files',
    'split-compress': 'Split & Compress PDFs',
    'text-to-pdf': 'Text to PDF'
  };
  return labels[op] || op;
}

function StatCard({ label, value, subtitle }: { label: string; value: string | number; subtitle?: string }) {
  return (
    <div className="bg-[#f5f5f7] rounded-lg p-4 lg:p-6 border border-black/[0.06]">
      <p className="text-[#86868b] text-xs lg:text-sm uppercase tracking-wide">{label}</p>
      <p className="text-2xl lg:text-4xl font-semibold mt-2">{value}</p>
      {subtitle && <p className="text-[#86868b] text-xs lg:text-sm mt-1">{subtitle}</p>}
    </div>
  );
}
